import React, { useEffect, useState } from "react";
import { formatUsd } from "@veyso/core";
import { api, type BillingView } from "../api.js";
import { capture } from "../lib/posthog.js";

/**
 * Credit: what is left, how to add more, and where it went.
 *
 * No Save button: adding credit takes effect the
 * moment Stripe says it did, and a form you could lose by navigating away is
 * the wrong shape for money.
 *
 * The statement at the bottom is the part that earns its place. It is the only
 * record of a video once the project has been deleted, and "where did my credit
 * go" is the question somebody actually opens this page to answer.
 */
const seconds = (value: number | null): string => {
  if (value === null) return "";
  const minutes = Math.floor(value / 60);
  return `${minutes}:${String(Math.round(value % 60)).padStart(2, "0")}`;
};

const when = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export const BillingPanel: React.FC = () => {
  const [view, setView] = useState<BillingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const [confirmed, setConfirmed] = useState<number | null>(null);

  useEffect(() => {
    /**
     * Coming back from Stripe, ask whether the money arrived.
     *
     * `?checkout=cs_…` is put there by the success URL. It is a name and not a
     * claim — the server asks Stripe about that session and checks it belongs
     * to this account — so a pasted or stale id gets nothing.
     *
     * Then the balance is loaded, in that order: loading first would show the
     * old number and leave somebody who has just paid looking at the figure
     * they came here to change.
     *
     * The id is taken out of the URL afterwards so a refresh is a refresh
     * rather than a second confirmation, and so the address is not a thing to
     * share by accident.
     */
    const load = async (): Promise<void> => {
      const url = new URL(window.location.href);
      const sessionId = url.searchParams.get("checkout");

      if (sessionId) {
        try {
          const result = await api.confirmTopUp(sessionId);
          if (result.granted && result.amountUsd) setConfirmed(result.amountUsd);
        } catch {
          // The balance below is the answer that matters. A confirmation that
          // failed while the webhook succeeded looks identical from here, and
          // saying "something went wrong" over a balance that did go up would
          // be the wrong half of the truth.
        }
        url.searchParams.delete("checkout");
        window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
      }

      try {
        setView(await api.billing());
      } catch (cause) {
        setError((cause as Error).message);
      }
    };

    void load();
  }, []);

  const topUp = async (amountUsd: number) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.startTopUp(amountUsd);
      capture("topup_started", { amount_usd: amountUsd });
      // A navigation, not a new tab. Stripe's page is where the person is now,
      // and coming back is a return rather than closing something.
      window.location.href = url;
    } catch (cause) {
      setError((cause as Error).message);
      setBusy(false);
    }
  };

  if (error && !view) return <p className="text-[12px] text-signal-bad">{error}</p>;
  if (!view) return null;

  if (!view.billing) {
    return (
      <section className="flex flex-col gap-2">
        <h1 className="text-[22px] font-semibold tracking-tight text-ink-100">Billing</h1>
        <p className="max-w-[56ch] text-[13px] leading-[1.6] text-ink-300">
          {
            "This install has no accounts, so there is nothing to bill. Everything runs on your own provider keys and costs whatever they charge you."
          }
        </p>
      </section>
    );
  }

  const onFreeTier = view.freeRemaining > 0;
  const freeForever = view.freeForever === true;

  return (
    <section className="flex flex-col gap-5">
      <div>
        <h1 className="text-[22px] font-semibold tracking-tight text-ink-100">Billing</h1>
        <p className="mt-1 text-[13px] text-ink-300">
          {freeForever
            ? "Nothing this team makes is charged."
            : "Credit is spent per export and never expires. Nothing renews."}
        </p>
      </div>

      {/* Only when this page was the one that granted it. A payment the webhook
          had already handled arrives as a balance that is simply correct, and
          announcing it again would be telling somebody about a thing that
          happened while they were on Stripe's page. */}
      {confirmed !== null ? (
        <p className="rounded-[10px] border border-[var(--edge)] bg-bay-850 px-4 py-3 text-[13px] text-ink-100">
          {`Added $${confirmed.toFixed(2)} of credit. It is in the balance below.`}
        </p>
      ) : null}

      {/* A team set free forever has no allowance to count and no balance to
          top up: one card saying so, and the statement below still lists what
          was made. */}
      {freeForever ? (
        <div className="rounded-[10px] border border-[var(--edge)] bg-bay-850 p-4">
          <p className="text-[11px] uppercase tracking-wide text-ink-400">Plan</p>
          <p className="mt-1 text-[22px] font-semibold text-ink-100">{"Free forever"}</p>
          <p className="mt-1 text-[12px] text-ink-300">
            {"Everything this team makes is free: videos, exports, languages and launch film versions."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-[10px] border border-[var(--edge)] bg-bay-850 p-4">
            <p className="text-[11px] uppercase tracking-wide text-ink-400">Free videos</p>
            <p className="tnum mt-1 text-[22px] font-semibold text-ink-100">
              {view.freeVideoLimit - view.freeRemaining}/{view.freeVideoLimit}
            </p>
            <p className="mt-1 text-[12px] text-ink-300">
              {onFreeTier
                ? `${view.freeRemaining} left, up to 5 minutes of footage each.`
                : "All used. Exports draw from credit now."}
            </p>
          </div>

          <div className="rounded-[10px] border border-[var(--edge)] bg-bay-850 p-4">
            <p className="text-[11px] uppercase tracking-wide text-ink-400">Credit</p>
            <p className="tnum mt-1 text-[22px] font-semibold text-ink-100">{formatUsd(view.balanceUsd)}</p>
            <p className="mt-1 text-[12px] text-ink-300">{"Spent per export. Never expires."}</p>
          </div>
        </div>
      )}

      {freeForever ? null : view.canTopUp ? (
        <div className="rounded-[10px] border border-[var(--edge)] bg-bay-850 p-4">
          <h2 className="text-[13px] font-semibold text-ink-100">{"Add credit"}</h2>
          <p className="mt-1 text-[12px] text-ink-300">
            {"You will be taken to Stripe. Card details never touch our servers."}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {view.presetsUsd.map((amount) => (
              <button
                key={amount}
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => void topUp(amount)}
              >
                {formatUsd(amount)}
              </button>
            ))}

            <span className="mx-1 text-[12px] text-ink-400">or</span>

            <input
              className="input w-[7rem]"
              inputMode="decimal"
              placeholder="Amount"
              value={custom}
              disabled={busy}
              onChange={(event) => setCustom(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void topUp(Number(custom));
                }
              }}
              aria-label="A different amount, in dollars"
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || custom.trim().length === 0}
              onClick={() => void topUp(Number(custom))}
            >
              {busy ? "Opening Stripe…" : "Add"}
            </button>
          </div>

          {error ? <p className="mt-2.5 text-[12px] text-signal-bad">{error}</p> : null}
        </div>
      ) : (
        <p className="text-[12px] text-ink-400">
          {"Payments are not set up on this deployment yet, so credit cannot be added from here."}
        </p>
      )}

      <div>
        <h2 className="text-[13px] font-semibold text-ink-100">{"Statement"}</h2>
        {view.ledger.length === 0 ? (
          <p className="mt-1 text-[12px] text-ink-300">{"Nothing yet."}</p>
        ) : (
          <ul className="mt-2 flex flex-col">
            {view.ledger.map((row) => (
              <li
                key={row.id}
                className="flex items-baseline justify-between gap-4 border-b border-[var(--edge)] py-2.5 text-[12.5px] last:border-b-0"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-ink-100">
                    {row.kind === "purchase"
                      ? "Credit added"
                      : row.kind === "export"
                        ? (row.projectName ?? "A video")
                        : row.kind === "render"
                          ? // Named for what it was, because it sits beside a
                            // line for the same project and two identical
                            // labels would read as a double charge.
                            `${row.projectName ?? "A video"} — rendered on our servers`
                          : row.kind === "revision"
                            ? `${row.projectName ?? "A video"} — ${row.note ?? "a new version"}`
                            : row.kind === "translation"
                              ? `${row.projectName ?? "A video"} — ${row.note ?? "a language"}`
                              : row.kind === "refund"
                                ? "Refund"
                                : (row.note ?? "Adjustment")}
                  </span>
                  <span className="text-[11px] text-ink-400">
                    {when(row.createdAt)}
                    {(row.kind === "export" || row.kind === "render") && row.durationSeconds !== null
                      ? ` · ${seconds(row.durationSeconds)}`
                      : ""}
                    {row.usedFreeVideo ? " · free video" : ""}
                    {row.freeForever ? " · free forever" : ""}
                  </span>
                </span>
                <span className={`tnum shrink-0 font-medium ${row.amountUsd > 0 ? "text-signal-ok" : "text-ink-200"}`}>
                  {row.usedFreeVideo || row.freeForever
                    ? "On us"
                    : `${row.amountUsd > 0 ? "+" : "−"}${formatUsd(Math.abs(row.amountUsd))}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};
